import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Button,
  SimpleGrid,
  Card,
  Text,
  Heading,
  Spinner,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  HStack,
  Select,
  IconButton,
  Spacer,
  InputGroup,
  Input,
  InputRightElement,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { FiSearch } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchSecurityDeposits,
  setCurrentType,
} from "../../redux/SecurityDepositSlice";
import DisplayData from "../../components/DisplayData";

const SecurityList = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { customerlist, loading, currentType } = useSelector(
    (state) => state.sd_deposit
  );

  const primaryColor = "#347433";

  const tabs = [
    { label: "Applications", type: "application" },
    { label: "Paid Deposits", type: "paid" },
  ];

  // Filter data based on search term
  const filteredData =
  customerlist?.data?.filter((customer) =>
    (customer?.customer_name || customer?.customer_business_name || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  ) || [];


  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredData.slice(startIndex, endIndex);

  
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, itemsPerPage, searchTerm]);

  useEffect(() => {
    dispatch(
      fetchSecurityDeposits({
        type: "application",
        page: 1,
        limit: 100,
        userid: 1,
      })
    );
    dispatch(setCurrentType("application"));
  }, [dispatch]);

  const handleTabChange = (index) => {
    setActiveTab(index);
    const selectedTab = tabs[index];
    dispatch(setCurrentType(selectedTab.type));
    dispatch(
      fetchSecurityDeposits({
        type: selectedTab.type,
        page: 1,
        limit: 100,
        userid: 1,
      })
    );
  };

  const handleViewDetails = (customerId, orderid) => {
    navigate("security-detail", {
      state: { customerId, orderid, type: currentType },
    });
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (e) => setItemsPerPage(parseInt(e.target.value));

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages)
      startPage = Math.max(1, endPage - maxVisiblePages + 1);

    buttons.push(
      <IconButton
        key="prev"
        icon={<ChevronLeftIcon />}
        isDisabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
        variant="outline"
        colorScheme="green"
        size="sm"
      />
    );

    if (startPage > 1) {
      buttons.push(
        <Button
          key={1}
          onClick={() => handlePageChange(1)}
          variant={1 === currentPage ? "solid" : "outline"}
          colorScheme="green"
          size="sm"
        >
          1
        </Button>
      );
      if (startPage > 2) buttons.push(<Text key="ellipsis1" px={2}>...</Text>);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          onClick={() => handlePageChange(i)}
          variant={i === currentPage ? "solid" : "outline"}
          colorScheme="green"
          size="sm"
        >
          {i}
        </Button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) buttons.push(<Text key="ellipsis2" px={2}>...</Text>);
      buttons.push(
        <Button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          variant={totalPages === currentPage ? "solid" : "outline"}
          colorScheme="green"
          size="sm"
        >
          {totalPages}
        </Button>
      );
    }

    buttons.push(
      <IconButton
        key="next"
        icon={<ChevronRightIcon />}
        isDisabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
        variant="outline"
        colorScheme="green"
        size="sm"
      />
    );

    return buttons;
  };

  return (
    <>
      {/* Header with Search */}
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        display="flex"
        flexDirection={["column", "column", "row"]}
        alignItems={["stretch", "stretch", "center"]}
        gap={3}
        flexWrap="wrap"
      >
        <Heading fontSize="xl">Security Deposit</Heading>
        <Spacer display={["none", "none", "block"]} />

        <Flex align="center" gap={3} flexWrap="wrap">
        <Select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            placeholder=""
            size="md"
            w={["100%", "80px"]}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </Select>
          {/* ✅ Your Custom Search Bar */}
          <InputGroup
            bg="#f4f5f7"
            borderRadius={5}
            w={["100%", "auto"]}
            maxW="sm"
          >
            <Input
              placeholder="Search by vendor name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              border="none"
              _focus={{ boxShadow: "none" }}
            />
            <InputRightElement>
              <FiSearch color="#555" />
            </InputRightElement>
          </InputGroup>

        
        </Flex>
      </Card>

      {/* Tabs Section */}
      <Tabs
        variant="soft-rounded"
        colorScheme="green"
        index={activeTab}
        onChange={handleTabChange}
      >
        <TabList flexWrap="wrap" gap={2} overflowX="auto" py={2}>
          {tabs.map((tab, i) => (
            <Tab
              key={i}
              fontWeight="medium"
              fontSize={{ base: "xs", md: "sm" }}
              px={{ base: 3, md: 4 }}
              py={{ base: 1, md: 2 }}
              _selected={{ color: "white", bg: primaryColor }}
              whiteSpace="nowrap"
            >
              {tab.label}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {tabs.map((tab, i) => (
            <TabPanel key={i} px={0}>
              {loading ? (
                <Flex justify="center" align="center" py={8}>
                  <Spinner size="lg" color="green.500" />
                </Flex>
              ) : currentItems.length > 0 ? (
                <>
                  <SimpleGrid minChildWidth="xs" spacing="10px">
  {currentItems.map((customer) => (
    <Card
      key={customer.id}
      paddingInline={5}
      paddingBlock={2}  // ✅ matches vendor/customer cards
      position="relative"
      cursor="pointer"
      bg="white"
      onClick={() => handleViewDetails(customer.userid, customer.orderid)}
      _hover={{ boxShadow: "md", transform: "translateY(-3px)" }}
      transition="all 0.2s ease"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      {/* Header */}
      <DisplayData
        fs="16px"
        fw="bold"
        v_color="#007bc9"
        value={customer.customer_name || "NA"}
      />

      {/* Key–Value Section */}
      <Box mt={1}>
        {[
          { title: "Order ID", value: customer.orderid },
          { title: "Date", value: customer.datetime },
          { title: "Status", value: customer.status },
          {
            title: "Amount",
            value: customer.amount ? `₹${customer.amount}` : "NA",
          },
        ].map((item, idx) => (
          <Flex
            key={idx}
            justify="space-between"
            align="center"
            mb="1"
            wrap="nowrap"
          >
            <Text fontSize="sm" color="gray.600" flex="1" noOfLines={1}>
              {item.title}
            </Text>
            <Text
              fontSize="sm"
              color="gray.800"
              flex="1"
              textAlign="right"
              noOfLines={1}
              fontWeight={item.title === "Amount" ? "semibold" : "normal"}
            >
              {item.value || "NA"}
            </Text>
          </Flex>
        ))}
      </Box>
    </Card>
  ))}
</SimpleGrid>


                  {/* Pagination */}
                  {/* {totalPages > 1 && (
                    <Flex justify="center" mt={8}>
                      <HStack spacing={1} flexWrap="wrap">
                        {renderPaginationButtons()}
                      </HStack>
                    </Flex>
                  )} */}
                </>
              ) : (
                <Flex justify="center" align="center" py={8}>
                  <Text color="gray.500" fontSize="lg">
                    No {tab.label.toLowerCase()} found
                  </Text>
                </Flex>
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </>
  );
};

export default SecurityList;
